import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import CalcSection from './CalcSection'
import CalcParagraph from './CalcParagraph'
import NavBtn from './NavBtn'
import {
  basicYear1AnnuityValueSelector,
  extrasYear1AnnuityValueSelector,
  freedomYear1AnnuityValueSelector,
  pvBasicAdditionalCapitalSelector,
  pvExtrasAdditionalCapitalSelector,
  pvFreedomAdditionalCapitalSelector,
  basicMonthlySavingsSelector,
  extrasMonthlySavingsSelector,
  freedomMonthlySavingsSelector
} from '../../redux/selectors'
import * as forms from '../forms/form'
import { formatWithCommas } from '../foundation/number'
import { getSectionTotalKey } from './section'
import { otherIncomeSectionId } from './Section7Income'
import LabeledRowOutput from '../forms/LabeledRowOutput'
import Section8RowLabel from './Section8RowLabel'
import DisplayDynamicList from '../forms/DisplayDynamicList'
import ALink from './ALink'
import '../styles/section8Style.css'
import emailButton from '../images/email-icon-33.jpg'
import { fbSetup, twitterSetup, emailAddr, mailToEmailAddr } from '../social.js'
import HelpFilter from './HelpFilter'

/**
 * Smart component for rendering Section 8 of the calculator
 */
class Section8 extends Component {
  componentDidMount () {
    fbSetup(document, 'script', 'facebook-jssdk')
    window.twttr = twitterSetup(document, 'script', 'twitter-wjs')
  }
  /**
   * Render component
   */
  render () {
    const userInputStyle = {
      color: '#00B084'
    }

    const bottomBuffer = {
      width: '100%',
      height: 40
    }

    const goBackSection = {
      display: 'flex',
      flexFlow: 'row nowrap',
      justifyContent: 'flex-start',
      marginBottom: 20
    }

    const goBackText = {
      textIndent: 20,
      maxWidth: 844
    }

    const goBackBtn = {
      padding: '32px 0 0 20px'
    }

    const socialLinks = {
      display: 'flex',
      flexFlow: 'column nowrap',
      justifyContent: 'center',
      padding: '0 20px 0 20px'
    }

    const socialLinksText = {
      maxWidth: 280,
      padding: '30px 0 30px 20px'
    }

    // const pintrestStyle = {
    //   margin: '0 10px -2px 10px'
    // }

    const yearsStr = this.props.yearsUntilRetirement === 1 ? 'year' : 'years'

    return (
      <CalcSection title={this.props.title}>
        Here's what you told me...
        <CalcParagraph>
          You want to retire in{' '}
          <span style={userInputStyle}>{this.props.yearsUntilRetirement}</span>{' '}
          years and expect to be in retirement for approximately{' '}
          <span style={userInputStyle}>{this.props.yearsInRetirement}</span>{' '}
          years. During this time you expect the average inflation rate to be
          around{' '}
          <span style={userInputStyle}>{this.props.inflation * 100}%</span>.
        </CalcParagraph>
        <CalcParagraph>
          You currently have{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.taxDeferredSavings)}
          </span>{' '}
          in tax-deferred savings available to fund your retirement and you
          expect a{' '}
          <span style={userInputStyle}>
            {formatWithCommas(this.props.taxRate * 100)}%
          </span>{' '}
          tax rate on distributions. Before you retire you expect your savings
          to earn an average annual return of{' '}
          <span style={userInputStyle}>
            {(this.props.preAssetsReturn * 100).toFixed(2)}%
          </span>{' '}
          and after retirement you expect your savings to earn{' '}
          <span style={userInputStyle}>
            {(this.props.postAssetsReturn * 100).toFixed(2)}%
          </span>.
        </CalcParagraph>
        <CalcParagraph>
          In retirement, each month you expect to have{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.socialSecurity)}
          </span>{' '}
          in Social Security income,{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.pension)}
          </span>{' '}
          from a pension and{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.otherIncome)}
          </span>{' '}
          from other income.
        </CalcParagraph>
        <CalcParagraph>
          You expect your monthly costs to look like this in retirement:
        </CalcParagraph>
        <Section8RowLabel style={userInputStyle} text='Basics:' />
        <LabeledRowOutput
          label={forms.housingLabel}
          output={formatWithCommas(this.props.housing)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.utilitiesLabel}
          output={formatWithCommas(this.props.utilities)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.foodLabel}
          output={formatWithCommas(this.props.food)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.transportationLabel}
          output={formatWithCommas(this.props.transportation)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.insuranceLabel}
          output={formatWithCommas(this.props.insurance)}
          altLayout
        />
        <LabeledRowOutput
          label='Total'
          output={formatWithCommas(this.props.section1Total)}
          altLayout
          rowStyle={userInputStyle}
        />
        <Section8RowLabel style={userInputStyle} text='Extras:' />
        <LabeledRowOutput
          label={forms.clothesLabel}
          output={formatWithCommas(this.props.clothes)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.diningEntLabel}
          output={formatWithCommas(this.props.diningEnt)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.digitalSvcsLabel}
          output={formatWithCommas(this.props.digitalSvcs)}
          altLayout
        />
        <LabeledRowOutput
          label={forms.giftsLabel}
          output={formatWithCommas(this.props.gifts)}
          altLayout
        />
        <LabeledRowOutput
          label='Total'
          output={formatWithCommas(this.props.section2Total)}
          altLayout
          rowStyle={userInputStyle}
        />
        <LabeledRowOutput
          label='Basics + Extras'
          output={formatWithCommas(
            this.props.section1Total + this.props.section2Total
          )}
          altLayout
          rowStyle={userInputStyle}
        />
        <Section8RowLabel style={userInputStyle} text='Freedom:' />
        <DisplayDynamicList
          reduxFormLinesKey='section3FormLines'
          sectionNumber={3}
          altLayout
        />
        <LabeledRowOutput
          label='Total'
          output={formatWithCommas(this.props.section3Total)}
          altLayout
          rowStyle={userInputStyle}
        />
        <LabeledRowOutput
          label='Basics + Extras + Freedom'
          output={formatWithCommas(
            this.props.section1Total +
              this.props.section2Total +
              this.props.section3Total
          )}
          altLayout
          rowStyle={userInputStyle}
        />
        <div style={bottomBuffer} />
        <div style={goBackSection}>
          <div style={goBackText}>
            Feel free to go back and change the numbers if anything looks wrong.
            If you are just second guessing yourself, second-guess your second
            guessing before going back.
          </div>
          <div style={goBackBtn}>
            <NavBtn nextSection={7} back />
          </div>
        </div>
        <CalcParagraph>
          Here are the numbers: when you retire in{' '}
          <span style={userInputStyle}>{this.props.yearsUntilRetirement}</span>
          {` ${yearsStr} `} you will need{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.basicYear1Annuity)}
          </span>{' '}
          in savings at that time to fully fund your{' '}
          <span style={userInputStyle}>Basic</span> monthly costs. If the key
          assumption, pre-retirement savings return rate (<span
            style={userInputStyle}
          >
            {(this.props.preAssetsReturn * 100).toFixed(2)}%
          </span>), holds true, you will need to save{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.basicMonthlySavings * 12)}
          </span>{' '}
          each year, or{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.basicMonthlySavings)}
          </span>{' '}
          per month, to meet this goal.
        </CalcParagraph>
        <CalcParagraph>
          To cover <span style={userInputStyle}>Basics{' & '}Extras</span>{' '}
          you'll need a total of{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.extrasYear1Annuity)}
          </span>{' '}
          when you retire. You can get that by saving{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.extrasMonthlySavings * 12)}
          </span>{' '}
          each year, or{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.extrasMonthlySavings)}
          </span>{' '}
          per month. And finally, to acheive{' '}
          <span style={userInputStyle}>Finance Freedom!</span> you should have{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.freedomYear1Annuity)}
          </span>{' '}
          in the bank which you can do by saving{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.freedomMonthlySavings * 12)}
          </span>{' '}
          each year, or{' '}
          <span style={userInputStyle}>
            ${formatWithCommas(this.props.freedomMonthlySavings)}
          </span>{' '}
          per month.
        </CalcParagraph>
        <CalcParagraph>Here is the breakdown in list form:</CalcParagraph>
        <Section8RowLabel style={userInputStyle} text='Basics' />
        <LabeledRowOutput
          label='Retirement Total'
          output={formatWithCommas(this.props.basicYear1Annuity)}
          altLayout
        />
        <LabeledRowOutput
          label='Save Annually'
          output={formatWithCommas(this.props.basicMonthlySavings * 12)}
          altLayout
        />
        <LabeledRowOutput
          label='Save Monthly'
          output={formatWithCommas(this.props.basicMonthlySavings)}
          altLayout
        />
        <Section8RowLabel style={userInputStyle} text='Basics & Extras' />
        <LabeledRowOutput
          label='Retirement Total'
          output={formatWithCommas(this.props.extrasYear1Annuity)}
          altLayout
        />
        <LabeledRowOutput
          label='Save Annually'
          output={formatWithCommas(this.props.extrasMonthlySavings * 12)}
          altLayout
        />
        <LabeledRowOutput
          label='Save Monthly'
          output={formatWithCommas(this.props.extrasMonthlySavings)}
          altLayout
        />
        <Section8RowLabel style={userInputStyle} text='Finance Freedom!' />
        <LabeledRowOutput
          label='Retirement Total'
          output={formatWithCommas(this.props.freedomYear1Annuity)}
          altLayout
        />
        <LabeledRowOutput
          label='Save Annually'
          output={formatWithCommas(this.props.freedomMonthlySavings * 12)}
          altLayout
        />
        <LabeledRowOutput
          label='Save Monthly'
          output={formatWithCommas(this.props.freedomMonthlySavings)}
          altLayout
        />
        <div style={bottomBuffer} />
        <CalcParagraph>
          If you've put some thought into your answers and were acurate where
          possible, then these numbers should reflect a decent estimate of what
          it will take to provide you a comfortable retirement. It is worth
          repeating that out of necessity you were asked to make some pretty
          significant assumptions. Unfortunately that is how you have to play
          the game - life isn't always certain and it can be messy. The best
          strategy to hedge against the risk of your assumptions being wrong is
          to crunch the numbers regularly. I would suggest annually running this
          calculator to see where you're at and adjusting as necesary. The
          affect of doing this means that your current savings at that time plus
          all other assumptions will have "auto-corrected" to the future reality
          and as retirement approaches, you'll have more certainty given the
          shorter planning horizon.
        </CalcParagraph>
        <CalcParagraph>
          <HelpFilter showLevel={[1, 2]}>
            Closing thought: we've touched on a lot of important topics along
            the way here. Personal finance has seemingly become quite complex in
            modern times. There's an amusing story about a University of Chicago
            professor, Harold Pollack, who made an off-hand comment in an
            interview a few years back that the best personal finance advice
            "...can fit on a 3-by-5 index card and is available for free in the
            library..." When he was asked later where the index card was, he
            decided to follow through and wrote one up, took a picture of it and
            posted it on the web. It went viral and now he's written a book
            about it. Here's
            <ALink href='http://www.npr.org/sections/alltechconsidered/2016/01/08/462250239/when-an-index-card-of-financial-tips-isnt-enough-this-book-is-there'>
              a link to that story
            </ALink>{' '}
            (it has a picture of the post card). Here's the point: first,
            generally he's right and the post card is great advice for most
            people. Second, it isn't quite that simple because he wrote a book
            about it! He addresses this fact in the article linked above but to
            sum up, the note card has good advice but for the majority of
            people, they don't know <i>why</i> it's good advice. This makes it
            hard for most people to follow through on. I haven't read Harold's
            book although I plan to but I would encourage you to follow up on
            the advice on the note card in one way or another.
          </HelpFilter>
        </CalcParagraph>
        <div className='socialLinksContainer'>
          <div style={socialLinksText}>
            Finally, if you think I've provided value here, please share!
          </div>
          <div style={socialLinks}>
            <div
              className='fb-share-button'
              data-href='http://financefreedom.io'
              data-layout='button'
              data-size='large'
              data-mobile-iframe='true'
            >
              <a
                className='fb-xfbml-parse-ignore'
                target='_blank'
                rel='noopener noreferrer'
                href='https://www.facebook.com/sharer/sharer.php?u=http%3A%2F%2Ffinancefreedom.io%2F&amp;src=sdkpreparse'
              >
                Share
              </a>
            </div>
            <a
              href='https://twitter.com/intent/tweet?url=http%3A%2F%2Ffinancefreedom.io'
              className='twitter-share-button'
              data-show-count='false'
              data-size='large'
            >
              Tweet
            </a>
            {/* <a
              style={pintrestStyle}
              href='https://www.pinterest.com/pin/create/button/'
              target='_blank'
              rel='noopener noreferrer'
              data-pin-do='buttonPin'
              data-pin-tall='true'
              data-pin-round='true'
              data-pin-save='false'
            >
              <img
                src='//assets.pinterest.com/images/pidgets/pinit_fg_en_rect_gray_20.png'
                alt='Pintrest, pin it'
              />
            </a>
            <div className='g-plusone' data-size='standard' /> */}
          </div>
        </div>
        <CalcParagraph>
          And if you have feed back about the calculator, I'd love to hear that
          too. Email me with feature requests, bug reports or anything else
          you'd like to let me know. Thanks!
        </CalcParagraph>
        <div>
          <ALink href={mailToEmailAddr}>
            <img src={emailButton} alt='Email' />
          </ALink>
          <ALink href={mailToEmailAddr}>{emailAddr}</ALink>
        </div>
        <div className='socialLinksContainer'>
          <div style={socialLinksText} />
          <div style={socialLinks} />
        </div>
      </CalcSection>
    )
  }
}

Section8.propTypes = {
  title: PropTypes.string.isRequired
}

export function mapStateToProps (state) {
  return {
    yearsUntilRetirement: state.get('forms').get(forms.yearsUntilRetirementKey),
    yearsInRetirement: state.get('forms').get(forms.yearsInRetirementKey),
    inflation: state.get('forms').get(forms.inflationEstimateKey),
    taxDeferredSavings: state.get('forms').get(forms.taxDeferredSavingsKey),
    taxRate: state.get('forms').get(forms.taxRateKey),
    socialSecurity: state.get('forms').get(forms.ssIncomeKey),
    pension: state.get('forms').get(forms.pensionIncomeKey),
    otherIncome: state.get('app').get(getSectionTotalKey(otherIncomeSectionId)),
    housing: state.get('forms').get(forms.housingLabel),
    utilities: state.get('forms').get(forms.utilitiesLabel),
    food: state.get('forms').get(forms.foodLabel),
    transportation: state.get('forms').get(forms.transportationLabel),
    insurance: state.get('forms').get(forms.insuranceLabel),
    section1Total: state.get('app').get(getSectionTotalKey(1)),
    clothes: state.get('forms').get(forms.clothesLabel),
    diningEnt: state.get('forms').get(forms.diningEntKey),
    digitalSvcs: state.get('forms').get(forms.digitalSvcsKey),
    gifts: state.get('forms').get(forms.giftsLabel),
    section2Total: state.get('app').get(getSectionTotalKey(2)),
    section3Total: state.get('app').get(getSectionTotalKey(3)),
    preAssetsReturn: state.get('forms').get(forms.preAssetsReturnKey),
    postAssetsReturn: state.get('forms').get(forms.postAssetsReturnKey),
    basicYear1Annuity: basicYear1AnnuityValueSelector(state).toFixed(0),
    extrasYear1Annuity: extrasYear1AnnuityValueSelector(state).toFixed(0),
    freedomYear1Annuity: freedomYear1AnnuityValueSelector(state).toFixed(0),
    pvBasicAdditional: pvBasicAdditionalCapitalSelector(state).toFixed(0),
    pvExtrasAdditional: pvExtrasAdditionalCapitalSelector(state).toFixed(0),
    pvFreedomAdditional: pvFreedomAdditionalCapitalSelector(state).toFixed(0),
    basicMonthlySavings: basicMonthlySavingsSelector(state).toFixed(0),
    extrasMonthlySavings: extrasMonthlySavingsSelector(state).toFixed(0),
    freedomMonthlySavings: freedomMonthlySavingsSelector(state).toFixed(0)
  }
}

export default connect(mapStateToProps)(Section8)



// WEBPACK FOOTER //
// ./src/components/sections/Section8Results.js